<template>
  <div id="findings-copy" class="chart-copy">
    <h3 v-if="copyData[year].header.length > 0">{{ copyData[year].header }}</h3>
    <span>
      <p v-for="copy in copyData[year].copy" :key="copy" v-html="copy"></p>
    </span>
    <div class="disclaimers">
      <p>
        *Adapted from Kotelnikova et al. 2017<sup>1</sup> and Siffrin et al.
        2010.<sup>2</sup>
      </p>
      <p>
        <sup>†</sup>Siffrin et al. 2010<sup>2</sup> and Calabrese et al.
        2015.<sup>3</sup>
      </p>
    </div>
  </div>
</template>

<script>
import progressionMixin from "@/mixins/progressionMixin";

export default {
  name: "FindingsCopy",
  mixins: [progressionMixin],
  computed: {
    year() {
      return this.$store.state.year;
    },
  },
  data() {
    return {
      copyData: {
        0: {
          header: "",
          copy: [
            "The timelines shown in this graph illustrate typical disease progression over 20 years. As disease progresses in years, we see increasing impairment as assessed by the Disability Score, shown in the gray graph, in axonal degeneration, shown in the blue graph, and in cortical demyelination, shown in the orange graph. Peaks in the graph represent relapses, which decline over time as disease progresses.<sup>1,2</sup>",
          ],
        },
        1: {
          header: "Early Relapsing-Remitting Multiple Sclerosis (Early RRMS)",
          copy: [
            "RRMS is the most common type of MS, accounting for ~85% of cases.<sup>8</sup>",
            "It is characterized by discrete attacks that evolve over days to weeks, followed by some degree of recovery over weeks to months.<sup>8</sup>",
          ],
        },
        2: {
          header: "Early Relapsing-Remitting Multiple Sclerosis (Early RRMS)",
          copy: [
            "Peaks in the disability and demyelination graphs represent relapses.",
            "A relapse of MS is a patient reported, or objectively observed, event typical of an acute inflammatory demyelinating event in the central nervous system, with a duration of at least 24&nbsp;hours.<sup>11</sup>",
          ],
        },
        3: {
          header: "Early Relapsing-Remitting Multiple Sclerosis (Early RRMS)",
          copy: [
            "In between relapses, the disability and demyelination curves fall below the clinical threshold.<sup>1</sup>",
            "The patient may not have worsening neurological function, although some patients may still experience some degree of physical and cognitive decline, as illustrated by the slow increase in between relapse peaks on the graph.<sup>8</sup>",
          ],
        },
        4: {
          header: "Early Relapsing-Remitting Multiple Sclerosis (Early RRMS)",
          copy: [
            "The patient is currently experiencing another relapse as indicated by an acute peak in the disability and demyelination curves.",
            "Diagnosis of a relapse is predominantly clinical. Common symptoms include optic neuritis, sensory alterations, weakness, ataxia, fatigue, and cognitive difficulty.<sup>11</sup>",
          ],
        },
        5: {
          header: "Early Relapsing-Remitting Multiple Sclerosis (Early RRMS)",
          copy: [
            "The disability, demyelination, and axonal degeneration curves remain stable here.",
            "Residual disability is observed in one third to one half of all relapses.<sup>11</sup>",
          ],
        },
        6: {
          header: "Late Relapsing-Remitting Multiple Sclerosis (Late RRMS)",
          copy: [
            "The demyelination curve exhibited a small peak, but this was not reflected in peaks in the disability or axonal degeneration curves.  This indicates that the demyelination event was not reflected clinically or in terms of axonal degeneration.<sup>1</sup>",
            "Late RRMS is characterized by a disease duration of 6 to 10 years.<sup>3</sup>",
          ],
        },
        7: {
          header: "Late Relapsing-Remitting Multiple Sclerosis (Late RRMS)",
          copy: [
            "After a period of remission, the patient experiences another relapse.",
            "The frequency of relapses decreases with time from diagnosis and age.<sup>11</sup>",
          ],
        },
        8: {
          header: "Late Relapsing-Remitting Multiple Sclerosis (Late RRMS)",
          copy: [
            "With incomplete recovery following the last relapse, the three curves are holding steady during this time point.",
            "The phases between some relapses may exhibit no clinical progression.<sup>2</sup>",
            "Accumulated disability has just reached the clinical threshold level, defined as a score above 4.0 in the EDSS.<sup>1</sup>",
          ],
        },
        9: {
          header: "Late Relapsing-Remitting Multiple Sclerosis (Late RRMS)",
          copy: [
            "The three curves are relatively stable for this time point, with a small subclinical peak in the demyelination curve.",
          ],
        },
        10: {
          header: "Late Relapsing-Remitting Multiple Sclerosis (Late RRMS)",
          copy: [
            "The disability and demyelination curves have crossed the clinical threshold level, with axonal degeneration just below the threshold.",
            "The proportion of patients with RRMS who develop SPMS increases with longer disease duration, and most patients with RRMS develop SPMS over the long term.<sup>41</sup>",
          ],
        },
        11: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The patient experiences a clinical relapse at this timepoint, as shown by peaks in all three curves.",
            "SPMS is characterized by an initial relapse, followed by continuous gradual neurological deterioration not associated with acute attacks.<sup>2</sup>",
          ],
        },
        12: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves show a gradual increase over time. ",
            "The general consensus of practitioners has been that prior to the use of approved disease-modifying therapies, more than half of patients with RRMS will advance to an irreversible form of disease, or SPMS, within 10 to 20 years of onset.<sup>2,40</sup>",
          ],
        },
        13: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The disability, axonal degeneration, and demyelination curves gradually increase over time.",
            "Diagnosis of SPMS is typically retrospective, following a period of gradually accumulating disability, independent of relapses.<sup>45</sup>",
          ],
        },
        14: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves continue to increase above the clinical threshold, with no relapses.",
            "There is usually a period of uncertainty of ~2 years between the visit when disease progression is first suspected and when SPMS is finally diagnosed.<sup>44</sup>",
          ],
        },
        15: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves continue to increase, with a small relapse as shown by the peak in the disability curve.",
            "Patients with SPMS can still experience relapses, although at a lower annualized relapse rate compared to patients with RRMS.<sup>45<sup>",
          ],
        },
        16: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves continue to steadily increase.",
            "The prevalence and severity of all clinical manifestations are higher in patients with SPMS than in those with RRMS, especially with regards to muscle spasms, stiffness, bowel problems, and urinary incontinence.<sup>41</sup>",
          ],
        },
        17: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves continue to steadily increase.",
            "Patients with SPMS report more physical limitations in daily activities and poor health in general compared to patients with RRMS.<sup>41</sup>",
          ],
        },
        18: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves continue to steadily increase, with a small relapse as shown by a peak in the disability curve.",
            "MS disease course is variable from person to person and predicting a patient’s prognosis is challenging.<sup>41</sup>",
          ],
        },
        19: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "The three curves continue along their steady path of gradual increase.",
            "Early diagnosis and use of high-efficacy disease-modifying therapy are critical to help slow the progression of disease.<sup>41,51</sup>",
          ],
        },
        20: {
          header: "Secondary Progressive Multiple Sclerosis (SPMS)",
          copy: [
            "By exploring mobility, brain imaging, and neuronal health at different times throughout the MS disease continuum, we can better understand progression of physical symptoms in patients, while also gaining a deeper knowledge of the neuropathology that underlies these changes.",
          ],
        },
      },
    };
  },
};
</script>

<style lang="scss">
#findings-copy {
  // height: 180px;
  background: rgba($blue-dark2, 0.9);
  color: $white;
  padding: rem(10px) rem(20px);

  @include breakpoint(xs) {
    // width: 935px;
  }

  @include breakpoint(xs) {
    height: auto;
  }

  h3 {
    padding: 1rem 0;
    font-size: rem(15px);
  }

  p {
    margin-bottom: 0.5rem;
  }

  .disclaimers {
    margin-top: 1rem;
    p {
      font-size: rem(12px);
    }
  }
}

.chart-copy {
  min-height: 215px;
}
</style>